/**
 * softDelete — Sprint 2 (F5A-002, F5B-004)
 *
 * En lugar de `deleteDoc`, marca el documento con:
 *   eliminado: true
 *   fecha_eliminacion: serverTimestamp()
 *   eliminado_por: userId
 *
 * Las queries de listado deben filtrar con `where('eliminado','!=',true)` o
 * la ausencia del campo. Permite restauración y mantiene trazabilidad.
 *
 * Uso:
 *   await softDelete('propiedades', id, { userId, userEmail, reason });
 *
 * Para hard-delete irreversible (casos excepcionales), usar `hardDelete`.
 */

import { doc, updateDoc, deleteDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import { logAudit } from './auditLog';

export interface SoftDeleteOptions {
  userId?: string;
  userEmail?: string;
  userRol?: string;
  reason?: string;
  before?: any;
}

export async function softDelete(
  collectionName: string,
  docId: string,
  options: SoftDeleteOptions = {}
): Promise<void> {
  await updateDoc(doc(db, collectionName, docId), {
    eliminado: true,
    fecha_eliminacion: serverTimestamp(),
    eliminado_por: options.userId || null,
    eliminado_razon: options.reason || null,
  });

  await logAudit({
    action: 'soft_delete',
    collection: collectionName,
    docId,
    userId: options.userId,
    userEmail: options.userEmail,
    userRol: options.userRol,
    before: options.before,
    metadata: { reason: options.reason },
  });
}

/**
 * Hard delete irreversible. Usar solo cuando el modelo de datos no admite
 * soft delete (ej: limpiar duplicados confirmados).
 */
export async function hardDelete(
  collectionName: string,
  docId: string,
  options: SoftDeleteOptions = {}
): Promise<void> {
  await deleteDoc(doc(db, collectionName, docId));

  await logAudit({
    action: 'delete',
    collection: collectionName,
    docId,
    userId: options.userId,
    userEmail: options.userEmail,
    userRol: options.userRol,
    before: options.before,
    metadata: { reason: options.reason, irreversible: true },
  });
}

/**
 * Restaura un documento previamente soft-deleted.
 */
export async function restoreSoftDeleted(
  collectionName: string,
  docId: string,
  options: SoftDeleteOptions = {}
): Promise<void> {
  await updateDoc(doc(db, collectionName, docId), {
    eliminado: false,
    fecha_restauracion: serverTimestamp(),
    restaurado_por: options.userId || null,
  });

  await logAudit({
    action: 'restore',
    collection: collectionName,
    docId,
    userId: options.userId,
    userEmail: options.userEmail,
    userRol: options.userRol,
  });
}

export default softDelete;
